import { CancelTokenSource } from 'axios';
import { IAllProps as TinyMCEEditorProps } from '@tinymce/tinymce-react';
import { EventHandler } from '@tinymce/tinymce-react/lib/cjs/main/ts/Events';
import { ClassNameProp, ResponseErrorWithHandled } from '../types';
import { RequestStatus } from '../enums/request-status';
import { MergeTagOption } from '../components/overlay-merge-tags';
import {
  AttachmentDeletePayload,
  AttachmentUploadPayload,
  AttachmentUploadResponse,
} from '../../components/sequence/types';
import { ReplyForwardEditorAction } from '../../components/unified-inbox/type';

export type EditorProps = Partial<Pick<TinyMCEEditorProps, 'disabled'>> &
  Pick<TinyMCEEditorProps, 'value' | 'onEditorChange'> &
  ClassNameProp & {
    error?: boolean;
    onEditorBlur?: () => void;
    onAiColEnrichmentEditorClick?: () => void;
    showMergeAndVariableTags?: boolean;
    mergeTags?: MergeTagOption[];
    imageUploader?: ({ formData, onUploadProgress }) => any;
    onEditorInit?: () => void;
    getEditorInstance?: (editor: any) => void;
    shouldRenderTemplate?: boolean;
    showTemplateModal?: () => void;
    onKeyUp?: (e: any) => void;
    onKeyDown?: (e: React.KeyboardEvent) => void;
    addMergeTagInContent?: (option: any) => void;
    addVariableTagInContent?: (tag: string) => void;
    addSpintaxInContent?: (tag: string) => void;
    hideImageOptionOnToolbar?: boolean;
    showAddAttachments: boolean;
    attachments?: AttachmentType[];
    onAttachmentStatusChange?: (attachment: AttachmentType) => void;
    onAttachmentsChange?: (
      attachments: AttachmentType[],
      isAttachmentDeleted?: boolean,
    ) => void;
    sendAttachmentUploadRequest?: (
      payload: AttachmentUploadPayload,
      uniqueId?: number,
    ) => void; // UniqueId was added to handle concurrent API calls
    uploadedAttachment?: AttachmentUploadResponse;
    uploadAttachmentRequestStatus?: RequestStatus;
    uploadAttachmentRequestError?: ResponseErrorWithHandled;
    uploadAttachmentRequestMessage?: string;
    resetUploadAttachmentRequest?: (uniqueId?: number) => void;
    errorMessage?: string;
    sendDeleteAttachmentRequest?: (
      payload: AttachmentDeletePayload,
      uniqueId?: number,
    ) => void;
    deleteAttachmentRequestStatus?: RequestStatus;
    deleteAttachmentRequestError?: ResponseErrorWithHandled;
    deletedAttachmentId?: number;
    resetDeleteAttachmentRequest?: (uniqueId?: number) => void;
    onAttachmentsMetaChange?: (
      attachmentsMeta: AttachmentMetaDataType[],
    ) => void;
    isTemplateEditorModal?: boolean;
    sequenceStepVariantId?: number;
    spintaxOptions?: SpintaxOption[];
    mainList?: MainList[];
    showSpintaxDropdown?: boolean;
    disabledVariableTags?: string[];
    tagsDropdownClassName?: string;
    attachmentsClassname?: string;
    uniqueId?: number;
    action?: ReplyForwardEditorAction;
    disableRenderTemplateBtn?: boolean;
    maxInputCount?: number;
    errorMessages?: string[];
    isInline?: boolean;
    isToolbardynamic?: any;
    showVariableTag?: boolean;
    isCustomClass?: boolean;
    showDefaultContentStyle?: boolean;
    placeholder?: string;
    showCount?: boolean;
    onPaste?: (content: string) => void;
    showMinimalToolbar?: boolean; // toolbar with only 4 fields
    showContentStyle?: boolean;
    disableImagePaste?: boolean;
    customContentStyle?: string;
    singleLineContent?: boolean;
    hideHeader?: boolean;
    onTemplateApplied?: boolean;
    onTemplateAppliedReset?: () => void;
    sendThisEmailInSameThreadCheckbox?: boolean;
    disableSpinTagConversion?: boolean; // Disable automatic conversion of spin tags to chips
    treatMergeTagAsVariableTag?: boolean;
  };

export type VisibilityOption = {
  key: string;
  label: string;
  header: string;
  description: string;
  icon: string;
};

export type MentionOption = {
  id: string;
  label: string;
  value: string;
};

export type MentionProps = {
  key: string;
  label: string;
  tag: string;
};

export type InlineMentionEditorProps = {
  value: string;
  onEditorChange: (value: string) => void;
  className?: string;
  error?: boolean;
  onEditorBlur?: () => void;
  onAiColEnrichmentEditorClick?: () => void;
  disabled?: boolean;
  mergeTags?: MentionProps[];
  imageUploader?: ({ formData, onUploadProgress }) => any;
  onEditorInit?: () => void;
  getEditorInstance?: (editor: any) => void;
  onKeyUp?: (e: any) => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  showAddAttachments: boolean;
  attachments?: AttachmentType[];
  onAttachmentStatusChange?: (attachment: AttachmentType) => void;
  onAttachmentsChange?: (
    attachments: AttachmentType[],
    isAttachmentDeleted?: boolean,
  ) => void;
  sendAttachmentUploadRequest?: (
    payload: AttachmentUploadPayload,
    uniqueId?: number,
  ) => void;
  uploadedAttachment?: AttachmentUploadResponse;
  uploadAttachmentRequestStatus?: RequestStatus;
  uploadAttachmentRequestError?: ResponseErrorWithHandled;
  resetUploadAttachmentRequest?: (uniqueId?: number) => void;
  errorMessage?: string;
  sendDeleteAttachmentRequest?: (
    payload: AttachmentDeletePayload,
    uniqueId?: number,
  ) => void;
  deleteAttachmentRequestStatus?: RequestStatus;
  deleteAttachmentRequestError?: ResponseErrorWithHandled;
  deletedAttachmentId?: number;
  resetDeleteAttachmentRequest?: (uniqueId?: number) => void;
  onAttachmentsMetaChange?: (attachmentsMeta: AttachmentMetaDataType[]) => void;
  isTemplateEditorModal?: boolean;
  sequenceStepVariantId?: number;
  uniqueId?: number;
  action?: ReplyForwardEditorAction;
  attachmentsClassname?: string;
  placeholder?: string;
  hideHeader?: boolean;
  isInline?: boolean;
  onPaste?: (content: string) => void;
  showToolBarContainer?: boolean;
  showFooterToolbarContainer?: boolean;
  showFooterVisibilitySelector?: boolean;
  showFooterSaveButton?: boolean;
  showFooterCancelButton?: boolean;
  onFooterSave?: () => void;
  onFooterCancel?: () => void;
  saveButtonText?: string;
  cancelButtonText?: string;
  isFooterSaveButtonDisabled?: boolean;
  isFooterCancelButtonDisabled?: boolean;
  selectedVisibilityKey?: string;
  onVisibilityChange?: (key: string) => void;
  visibilityOptions?: VisibilityOption[];
  SaveBtnText?: string;
  showSkeleton?: boolean;
  autoFocus?: boolean;
  attachmentMsg?: boolean;
  notesTabAttachment?: boolean;
  draftSaved?: boolean;
  updateNoteRequestStatus?: string;
  createNoteRequestStatus?: string;
  showDiscardButton?: boolean;
  discardButtonText?: string;
  isFooterDiscardButtonDisabled?: boolean;
  onDiscard?: () => void;
  onEditClick?: () => void;
  blockedDraftText?: boolean;
  showEditIcon?: boolean;
  showDraftStatus?: boolean;
  showDraftSavedMessage?: boolean;
  setShowDraftSavedMessage?: React.Dispatch<React.SetStateAction<boolean>>;
};

export type MainList = {
  key: number;
  name: string;
  showInsertNewSpintax: boolean;
};
export type SpintaxOption = {
  key: number;
  name: string;
  category: string;
};

export enum AttachmentUploadStatus {
  Upload = 'upload',
  Uploading = 'uploading',
  UploadFailed = 'upload_failed',
  UploadSuccess = 'upload_success',
  Existing = 'existing',
  Deleting = 'deleting',
}

export type AttachmentType = {
  attachmentId: number;
  attachmentIdentifier?: string;
  status: AttachmentUploadStatus;
  file: any;
  attachmentSize?: number;
};

export type AttachmentMetaDataType = {
  attachmentIdentifier: string;
  percentage: number;
  source: CancelTokenSource;
};

export type TagsOptionsForInlineDropdown = {
  text: string;
  value: string;
  group?: string;
  type: 'merge tag' | 'variable tag';
  fieldType?: string;
  isAiGenerated?: boolean;
}[];
